import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Mic,
  Camera,
  BookOpen,
  Sun,
  Moon,
  Menu,
  X,
  History,
  LogOut,
  Settings,
} from 'lucide-react';
import { NavLink, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { AuthenticatedRouteErrorBoundary } from './AuthenticatedRouteErrorBoundary';
import './Layout.css';

export function Layout() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { signOut } = useAuth();

  useEffect(() => {
    const stored = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = stored || (prefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
    document.documentElement.setAttribute('data-theme', initialTheme);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const navItems = [
    { path: '/', label: 'Text', icon: LayoutDashboard },
    { path: '/voice', label: 'Voice', icon: Mic },
    { path: '/ocr', label: 'Camera', icon: Camera },
    { path: '/history', label: 'History', icon: History },
    { path: '/learn', label: 'Learn', icon: BookOpen },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="app">
      <header className="header" role="banner">
        <div className="header-content">
          <a href="/" className="logo" aria-label="ZoloTalk Home">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <circle cx="10" cy="16" r="9" stroke="currentColor" strokeWidth="2" />
              <circle cx="22" cy="16" r="9" stroke="currentColor" strokeWidth="2" />
              <path
                d="M10 16 L14 20 M14 20 L18 12 M18 12 L22 20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="logo-text">ZoloTalk</span>
          </a>

          <nav
            className={`nav ${mobileMenuOpen ? 'open' : ''}`}
            role="navigation"
            aria-label="Main navigation"
          >
            <ul className="nav-list">
              {navItems.map(({ path, label, icon: Icon }) => (
                <li key={path}>
                  <NavLink
                    to={path}
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                    aria-current={location.pathname === path ? 'page' : undefined}
                  >
                    <Icon size={20} aria-hidden="true" />
                    <span>{label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="header-actions">
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
              aria-pressed={theme === 'dark'}
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>

            <button className="theme-toggle" onClick={() => void signOut()} aria-label="Sign out">
              <LogOut size={20} aria-hidden="true" />
            </button>

            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="main-navigation"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <main className="main" id="main-content" role="main">
        <AuthenticatedRouteErrorBoundary>
          <Outlet />
        </AuthenticatedRouteErrorBoundary>
      </main>

      <footer className="footer" role="contentinfo">
        <p>ZoloTalk — Connecting the world, one tongue at a time</p>
      </footer>
    </div>
  );
}
